"use client";

/**
 * useHyperliquidOrderbook — live HL l2Book over Socket.IO.
 *
 * Backend `market-feed.ts` opens the HL `l2Book` WS lazily on first
 * subscribe, refcounted per asset. We just join `hl-orderbook:<assetId>`
 * and consume `hl:orderbook` events.
 *
 * HL l2Book pushes a flat `levels[2][]` shape (index 0 = bids, 1 = asks),
 * each level: `{ px, sz, n }`. We normalise to `Array<{ price, size }>`.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import {
  getSharedPriceSocket,
  getOrCreateSharedSocket,
  acquireSharedSocket,
  releaseSharedSocket,
} from "./usePriceStream";

export interface HlOrderbookLevel {
  price: number;
  size: number;
  numOrders: number;
}

export interface HlOrderbookSnapshot {
  assetId: number;
  bids: HlOrderbookLevel[];
  asks: HlOrderbookLevel[];
  timestamp: number;
}

interface Options {
  assetId: number | null | undefined;
  enabled?: boolean;
  /** Cap depth on the client to avoid pointless re-renders past N levels. */
  depth?: number;
}

interface Return {
  book: HlOrderbookSnapshot | null;
  isConnected: boolean;
}

export function useHyperliquidOrderbook({ assetId, enabled = true, depth = 20 }: Options): Return {
  const [book, setBook] = useState<HlOrderbookSnapshot | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const subscribedRef = useRef<string | null>(null);

  const channel = assetId ? `hl-orderbook:${assetId}` : null;

  useEffect(() => {
    if (!enabled || !channel || !assetId) return;

    let isMounted = true;
    let socket: ReturnType<typeof getSharedPriceSocket> = null;

    const handleConnect = () => {
      setIsConnected(true);
      // Re-emit subscribe on transport reconnect (silent-freeze, blip).
      if (socket && subscribedRef.current) {
        socket.emit("subscribe", [subscribedRef.current]);
      }
    };
    const handleDisconnect = () => setIsConnected(false);
    const handleBook = (msg: any) => {
      if (msg?.assetId !== assetId) return;
      const lvls: any[][] = Array.isArray(msg?.levels) ? msg.levels : [];
      const bidsRaw: any[] = Array.isArray(lvls[0]) ? lvls[0] : [];
      const asksRaw: any[] = Array.isArray(lvls[1]) ? lvls[1] : [];
      const norm = (rows: any[]): HlOrderbookLevel[] =>
        rows
          .map((r) => ({
            price: Number(r?.px ?? 0),
            size: Number(r?.sz ?? 0),
            numOrders: Number(r?.n ?? 0),
          }))
          .filter((l) => Number.isFinite(l.price) && l.size > 0)
          .slice(0, depth);
      setBook({
        assetId,
        bids: norm(bidsRaw),
        asks: norm(asksRaw),
        timestamp: Number(msg?.time) || Date.now(),
      });
    };

    const init = async () => {
      socket = getSharedPriceSocket() || (await getOrCreateSharedSocket());
      if (!socket || !isMounted) return;

      acquireSharedSocket();
      socket.on("connect", handleConnect);
      socket.on("disconnect", handleDisconnect);
      if (socket.connected) setIsConnected(true);

      socket.emit("subscribe", [channel]);
      subscribedRef.current = channel;
      socket.on("hl:orderbook", handleBook);
    };

    init();

    return () => {
      isMounted = false;
      if (socket) {
        if (subscribedRef.current) {
          socket.emit("unsubscribe", [subscribedRef.current]);
          subscribedRef.current = null;
        }
        socket.off("connect", handleConnect);
        socket.off("disconnect", handleDisconnect);
        socket.off("hl:orderbook", handleBook);
        releaseSharedSocket();
      }
    };
  }, [enabled, channel, assetId, depth]);

  return { book, isConnected };
}
